'use client';

/**
 * PlaceMenuNav — how a guest gets around a menu.
 *
 * THE MODE ROW HAS GONE. This used to open with a row of fulfilment chips
 * (Browse · Dine in · Pickup · Delivery · Pre-order) as the first thing under
 * the session ribbon. Three problems, all fatal to a first-time guest:
 *
 *   1. The default chip was "Browse", which is not a way of getting food — it is
 *      the absence of one. A guest who never touched the row (most of them) sat
 *      in a mode where every dish ended at "Scan the QR code at your table to
 *      order this", with no hint that "Pickup" one chip to the right would have
 *      worked.
 *   2. It asked a logistics question above a menu the guest had not read yet.
 *   3. It looked exactly like the category row beneath it — same pill, same
 *      size, same accent — so two rows of visually identical chips controlled
 *      two completely unrelated things.
 *
 * The question now lives in the action bar and is asked once, at the moment the
 * guest first puts something in the cart. See components/place/PlaceActionBar.tsx.
 *
 * WHAT IS LEFT: the menu's own structure, and the two levels are now told apart
 * by SHAPE rather than by position.
 *
 *   · Groups   — the menu's top-level divisions (breakfast service, drinks
 *                list). Underlined buttons select one group.
 *   · Sections — the categories inside the active group. Pills filter the list
 *                below.
 */

import { useEffect, useRef } from 'react';

import { cn } from '@/components/ui/utils';
import { zClass } from '@/lib/ui/z-layers';

export type PlaceNavSection = {
  id: string | null;
  label: string;
  count?: number;
};

export type PlaceNavGroup = {
  id: string | null;
  label: string;
};

export function PlaceMenuNav({
  sections,
  activeSectionId,
  onSectionChange,
  groups = [],
  activeGroupId,
  onGroupChange,
  stickyTop,
  className,
}: {
  sections: PlaceNavSection[];
  activeSectionId: string | null;
  onSectionChange: (id: string | null) => void;
  groups?: PlaceNavGroup[];
  activeGroupId?: string | null;
  onGroupChange?: (id: string | null) => void;
  stickyTop: string;
  className?: string;
}) {
  const showGroups = groups.length > 1 && Boolean(onGroupChange);
  const showSections = sections.length > 1;
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const active = list.querySelector<HTMLElement>('[data-active="true"]');
    if (!active) return;
    active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [activeSectionId]);

  if (!showGroups && !showSections) return null;

  return (
    <div
      className={cn(
        'sticky w-full',
        zClass('sticky'),
        'border-b border-[var(--color-border-subtle)]',
        'bg-[var(--color-bg-surface)]/95 backdrop-blur-xl',
        'font-[family-name:var(--font-place-control)]',
        className,
      )}
      style={{ top: stickyTop }}
    >
      <div className="mx-auto w-full [max-width:var(--place-measure,100%)]">
        {showGroups ? (
          <div
            role="group"
            aria-label="Menu groups"
            className="flex gap-4 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {groups.map((group) => {
              const active = group.id === activeGroupId;
              return (
                <button
                  key={group.id ?? '__all__'}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onGroupChange?.(group.id)}
                  className={cn(
                    // 44px floor (doc 05 §5.4). The underline still sits on the
                    // text's own baseline box, so the tab reads the same height.
                    'relative flex h-11 shrink-0 items-center whitespace-nowrap text-sm transition-colors',
                    'outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-accent)]',
                    active
                      ? 'font-semibold text-[var(--color-text-primary)]'
                      : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                  )}
                >
                  {group.label}
                  {active ? (
                    <span
                      aria-hidden="true"
                      className="absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-[var(--color-accent)]"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : null}

        {showSections ? (
          <div
            ref={listRef}
            role="group"
            aria-label="Menu sections"
            className={cn(
              // The 32px chips grew into 44px buttons, so the strip's own padding
              // comes down by the same 12px and the nav stays exactly as tall.
              'flex gap-1.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
              showGroups ? 'border-t border-[var(--color-border-subtle)]/60 pt-1' : 'pt-1',
            )}
          >
            {sections.map((section) => {
              const active = section.id === activeSectionId;
              return (
                <button
                  key={section.id ?? '__all__'}
                  type="button"
                  aria-pressed={active}
                  data-active={active}
                  onClick={() => onSectionChange(section.id)}
                  className={cn(
                    // The BUTTON is the hit area and is held to the 44px floor
                    // (doc 05 §5.4 — the guest surface answers to the same target
                    // size as the till). The chip inside stays 32px, so the row
                    // reads exactly as before and only the reachable area grows.
                    'group flex h-11 shrink-0 items-center rounded-full',
                    'outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-accent)]',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-8 items-center gap-1.5 rounded-full px-3.5 text-[13px] transition-colors duration-150',
                      active
                        ? 'bg-[var(--color-accent)] font-semibold text-[var(--color-accent-text)]'
                        : 'bg-[var(--color-bg-sunken)]/60 text-[var(--color-text-secondary)] group-hover:bg-[var(--color-bg-sunken)]',
                    )}
                  >
                    {section.label}
                    {section.count ? (
                      <span
                        className={cn(
                          'font-[family-name:var(--font-place-data)] text-[11px] tabular-nums',
                          active ? 'opacity-60' : 'opacity-40',
                        )}
                      >
                        {section.count}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
